'use client'

import { Span } from '@pcln/horizon'
import { useCallback, useRef, type KeyboardEvent } from 'react'

import { AI_SEARCH_EXAMPLES_LABEL, AI_SEARCH_TRY_ASKING_CHIPS } from './aiSearchConstants'

const EXAMPLE_CHIP_CLASS =
  'shrink-0 cursor-pointer rounded-action-sm border border-transparent bg-primary-1 px-2 py-[1px] text-body2 text-primary-13 shadow-md transition-all hover:bg-primary-3 hover:shadow-lg active:bg-primary-1'

const SUGGESTION_CHIPS_CONTAINER_CLASS = 'relative w-full min-h-9 min-w-0 px-1 py-1'

type AiSearchExamplesProps = {
  isMobile?: boolean
  onSelect: (label: string) => void
}

export function AiSearchSuggestions({ isMobile = false, onSelect }: Readonly<AiSearchExamplesProps>) {
  const chipButtonRefs = useRef<Array<HTMLButtonElement | null>>([])
  const chipRowClass = isMobile ? 'flex flex-wrap gap-2' : 'flex items-center gap-2'
  const chipsContainerClass = `${SUGGESTION_CHIPS_CONTAINER_CLASS} ${
    isMobile ? 'w-full' : 'overflow-x-auto overflow-y-hidden'
  }`

  const handleChipGroupKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return

    event.preventDefault()

    const currentIndex = chipButtonRefs.current.indexOf(document.activeElement as HTMLButtonElement)
    const fallbackIndex = event.key === 'ArrowRight' ? -1 : 0
    const nextIndex =
      event.key === 'ArrowRight'
        ? (currentIndex + 1 + AI_SEARCH_TRY_ASKING_CHIPS.length) % AI_SEARCH_TRY_ASKING_CHIPS.length
        : (currentIndex - 1 + AI_SEARCH_TRY_ASKING_CHIPS.length) % AI_SEARCH_TRY_ASKING_CHIPS.length

    chipButtonRefs.current[currentIndex === -1 ? fallbackIndex : nextIndex]?.focus()
  }, [])

  return (
    <div className='flex w-full flex-col gap-2' data-testid='ai-search-suggestions-layout'>
      <Span bold className='w-full' palette='neutral' shade='13' textStyle='body3'>
        {AI_SEARCH_EXAMPLES_LABEL}
      </Span>

      <div className={chipsContainerClass} data-testid='ai-search-chips-container'>
        <div
          aria-label={AI_SEARCH_EXAMPLES_LABEL}
          className={chipRowClass}
          data-testid='ai-search-chip-group'
          onKeyDown={handleChipGroupKeyDown}
          role='radiogroup'
          tabIndex={-1}
        >
          {AI_SEARCH_TRY_ASKING_CHIPS.map((label, index) => (
            <button
              className={EXAMPLE_CHIP_CLASS}
              data-chip-key={`default-${label}`}
              key={label}
              onClick={() => onSelect(label)}
              ref={(element) => {
                chipButtonRefs.current[index] = element
              }}
              type='button'
              role='radio'
              aria-checked='false'
            >
              <span>{label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
